import { useGetStrategistsHandover } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  DownloadIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import { EmptyState, SectionHeading, TableShell } from '@modules/Admin/Reporting/common';
import { StrategistHandoverDTO } from '@shared/apis/strategists.apis';
import { CONTRACT_STATUS_LABELS } from '@shared/constants';
import { ContractPaymentType, EnrollmentStatusTypes } from '@shared/enums';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { ReactNode, useState } from 'react';
import { downloadCsv } from '../AdmissionResults/admissionStats.helpers';
import { formatDate, formatMoney } from '../pages/Contracts/utils';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-4 py-2.5 text-left font-semibold';
const rightHead = 'px-4 py-2.5 text-right font-semibold';
const leftCell = 'px-4 py-2.5 text-left text-customLight';
const rightCell = 'px-4 py-2.5 text-right tabular-nums text-customLight';

const ENROLLMENT_STATUS_LABELS: Record<number, string> = {
  [EnrollmentStatusTypes.Active]: 'Active',
  [EnrollmentStatusTypes.Suspended]: 'Suspended',
  [EnrollmentStatusTypes.Canceled]: 'Canceled',
  [EnrollmentStatusTypes.Completed]: 'Completed',
  [EnrollmentStatusTypes.UnderObservation]: 'Under observation',
  [EnrollmentStatusTypes.WaitingForPayment]: 'Waiting for payment',
};

const Stat = (props: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'gold' | 'green';
}) => (
  <div className="flex flex-col rounded-xl bg-white/5 px-4 py-3">
    <span
      className={mergeClassNames(
        'text-lg font-semibold',
        props.tone === 'gold' && 'text-customGold',
        props.tone === 'green' && 'text-customGreen',
        !props.tone && 'text-white'
      )}
    >
      {props.value}
    </span>
    <span className="text-xs text-customGrey">{props.label}</span>
    {props.hint ? <span className="text-[11px] text-customGrey">{props.hint}</span> : null}
  </div>
);

const summaryCsv = (strategists: StrategistHandoverDTO[]) =>
  stringify([
    [
      'Strategist',
      'Email',
      'Mentor status',
      'Assigned students',
      'Students taught',
      'Enrollments to reallocate',
      'Courses to reallocate',
      'Earliest start',
      'Latest estimated end',
      'Months remaining',
      'Contracts',
      'Paid in full',
      'Installments',
      'Fully paid',
      'Contract totals',
      'Remaining to collect',
    ],
    ...strategists.map((row) => [
      row.mentor.fullName,
      row.mentor.email ?? '',
      row.mentor.status ?? '',
      row.assignedStudents,
      row.taughtStudents,
      row.enrollmentsToReallocate,
      row.coursesToReallocate.map((c) => `${c.courseTitle} (${c.count})`).join('; '),
      row.horizon.earliestStart ? moment(row.horizon.earliestStart).format('YYYY-MM-DD') : '',
      row.horizon.latestEnd ? moment(row.horizon.latestEnd).format('YYYY-MM-DD') : '',
      row.horizon.monthsRemaining ?? '',
      row.contracts.count,
      row.contracts.full,
      row.contracts.installments,
      row.contracts.fullyPaid,
      row.contracts.totals.map((t) => `${t.amount} ${t.currency}`).join('; '),
      row.contracts.totals.map((t) => `${t.remaining} ${t.currency}`).join('; '),
    ]),
  ]);

const detailCsv = (row: StrategistHandoverDTO) =>
  stringify([
    [
      'Type',
      'Student',
      'Course / Contract',
      'Status',
      'Start / Date',
      'Estimated end',
      'Sessions done',
      'Sessions total',
      'Amount',
      'Currency',
      'Payment type',
      'Installments',
      'Paid',
      'Remaining',
    ],
    ...row.enrollments.map((e) => [
      'Enrollment',
      e.studentName,
      e.courseTitle,
      ENROLLMENT_STATUS_LABELS[e.status] ?? e.status,
      e.startDate ? moment(e.startDate).format('YYYY-MM-DD') : '',
      e.estimatedEndDate ? moment(e.estimatedEndDate).format('YYYY-MM-DD') : '',
      e.sessionsDone,
      e.sessionsTotal,
      '',
      '',
      '',
      '',
      '',
      '',
    ]),
    ...row.contracts.rows.map((c) => [
      'Contract',
      c.studentName,
      c.contractNumber
        ? `No. ${c.contractNumber}`
        : c.source === 'hubspot'
        ? `HubSpot: ${c.hubspotDealName ?? ''}`
        : '',
      c.status !== undefined ? CONTRACT_STATUS_LABELS[c.status] ?? c.status : c.hubspotStage ?? '',
      c.contractDate ? moment(c.contractDate).format('YYYY-MM-DD') : '',
      '',
      '',
      '',
      c.amount,
      c.currency,
      c.paymentType === ContractPaymentType.Installments
        ? 'Installments'
        : c.paymentType === ContractPaymentType.Full
        ? 'Full'
        : 'Unknown',
      c.installmentsCount ?? '',
      c.paidAmount ?? '',
      c.remainingAmount ?? '',
    ]),
  ]);

const StrategistCard = (props: { row: StrategistHandoverDTO }) => {
  const { row } = props;
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col">
          <span className="text-lg font-semibold text-white">{row.mentor.fullName}</span>
          <span className="text-xs text-customGrey">
            {row.mentor.email ?? ''}
            {row.mentor.status ? ` · ${row.mentor.status}` : ''}
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            buttonText="Export details"
            icon={<DownloadIcon className="h-4 w-4" />}
            disabled={!row.enrollments.length && !row.contracts.rows.length}
            onClick={() =>
              downloadCsv(
                `strategist-${row.mentor.fullName.replace(/[^\w]+/g, '-')}-${moment().format(
                  'YYYY-MM-DD'
                )}.csv`,
                detailCsv(row)
              )
            }
          />
          <Button
            variant="secondary"
            buttonText={open ? 'Hide details' : 'Show details'}
            icon={
              open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />
            }
            onClick={() => setOpen((value) => !value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 tablet:grid-cols-3 laptop:grid-cols-6">
        <Stat
          label="Assigned students"
          value={row.assignedStudents}
          hint="Active students with this strategist set"
        />
        <Stat
          label="Students taught"
          value={row.taughtStudents}
          hint="In active enrollments as mentor"
        />
        <Stat
          label="Enrollments to reallocate"
          value={row.enrollmentsToReallocate}
          tone={row.enrollmentsToReallocate ? 'gold' : undefined}
        />
        <Stat
          label="Latest estimated end"
          value={row.horizon.latestEnd ? formatDate(row.horizon.latestEnd) : '—'}
          hint={
            row.horizon.monthsRemaining !== undefined
              ? `${row.horizon.monthsRemaining} months from today`
              : undefined
          }
        />
        <Stat
          label="Contracts"
          value={row.contracts.count}
          hint={`${row.contracts.full} paid in full · ${
            row.contracts.installments
          } in installments${
            row.contracts.unknownPaymentType ? ` · ${row.contracts.unknownPaymentType} unknown` : ''
          }${row.contracts.fromHubspot ? ` · ${row.contracts.fromHubspot} from HubSpot` : ''}`}
        />
        <Stat
          label="Fully paid"
          value={row.contracts.fullyPaid}
          tone={
            row.contracts.fullyPaid === row.contracts.count && row.contracts.count
              ? 'green'
              : undefined
          }
          hint="Nothing left to collect"
        />
      </div>

      <div className="flex flex-wrap gap-6 text-sm">
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-customGrey">
            Courses to reallocate
          </span>
          <div className="flex flex-wrap gap-1.5">
            {row.coursesToReallocate.length ? (
              row.coursesToReallocate.map((course) => (
                <span
                  key={course.courseTitle}
                  className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs text-customLight"
                >
                  {course.courseTitle} · {course.count}
                </span>
              ))
            ) : (
              <span className="text-xs text-customGrey">None</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide text-customGrey">Contract totals</span>
          <div className="flex flex-wrap gap-3">
            {row.contracts.totals.length ? (
              row.contracts.totals.map((total) => (
                <span key={total.currency} className="text-customLight">
                  <span className="font-semibold text-white">
                    {formatMoney(total.amount, total.currency)}
                  </span>
                  <span className="text-customGrey">
                    {' '}
                    · paid {formatMoney(total.paid, total.currency)} · remaining{' '}
                    {formatMoney(total.remaining, total.currency)}
                  </span>
                </span>
              ))
            ) : (
              <span className="text-xs text-customGrey">
                No contracts in Agora for these students
              </span>
            )}
          </div>
        </div>
      </div>

      {open ? (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <SectionHeading
              title={`Active enrollments (${row.enrollments.length})`}
              description="Enrollments where this strategist is the mentor. Sorted by estimated end date."
            />
            {!row.enrollments.length ? (
              <EmptyState>No active enrollments as mentor.</EmptyState>
            ) : (
              <TableShell minWidth="min-w-[820px]">
                <thead>
                  <tr className={headRow}>
                    <th className={leftHead}>Student</th>
                    <th className={leftHead}>Course</th>
                    <th className={leftHead}>Status</th>
                    <th className={leftHead}>Start</th>
                    <th className={leftHead}>Estimated end</th>
                    <th className={rightHead}>Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {row.enrollments.map((enrollment) => (
                    <tr key={enrollment._id} className={bodyRow}>
                      <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>
                        {enrollment.studentName}
                      </td>
                      <td className={leftCell}>{enrollment.courseTitle}</td>
                      <td className={leftCell}>
                        {ENROLLMENT_STATUS_LABELS[enrollment.status] ?? enrollment.status}
                      </td>
                      <td className={leftCell}>{formatDate(enrollment.startDate)}</td>
                      <td className={leftCell}>{formatDate(enrollment.estimatedEndDate)}</td>
                      <td className={rightCell}>
                        {enrollment.sessionsDone} / {enrollment.sessionsTotal}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <SectionHeading
              title={`Contracts (${row.contracts.rows.length})`}
              description="Agora contracts of the assigned and taught students. Students without an Agora contract show their HubSpot deal instead: amount from the closed-won Sales deal, payment type from the Contracts Pipeline stage."
            />
            {!row.contracts.rows.length ? (
              <EmptyState>No contracts recorded.</EmptyState>
            ) : (
              <TableShell minWidth="min-w-[920px]">
                <thead>
                  <tr className={headRow}>
                    <th className={leftHead}>Student</th>
                    <th className={leftHead}>Contract</th>
                    <th className={rightHead}>Amount</th>
                    <th className={leftHead}>Payment</th>
                    <th className={rightHead}>Paid</th>
                    <th className={rightHead}>Remaining</th>
                    <th className={leftHead}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {row.contracts.rows.map((contract) => (
                    <tr key={contract._id} className={bodyRow}>
                      <td className={mergeClassNames(leftCell, 'font-semibold text-white')}>
                        {contract.studentName}
                      </td>
                      <td className={leftCell}>
                        {contract.source === 'hubspot' ? (
                          <span className="text-customGold">HubSpot deal</span>
                        ) : contract.contractNumber ? (
                          `No. ${contract.contractNumber}`
                        ) : (
                          '—'
                        )}
                        <span className="block text-xs text-customGrey">
                          {contract.source === 'hubspot'
                            ? contract.hubspotDealName ?? formatDate(contract.contractDate)
                            : formatDate(contract.contractDate)}
                        </span>
                      </td>
                      <td className={rightCell}>
                        {formatMoney(contract.amount, contract.currency)}
                      </td>
                      <td className={leftCell}>
                        {contract.paymentType === ContractPaymentType.Installments
                          ? contract.installmentsCount
                            ? `${contract.installmentsCount} installments`
                            : 'Installments'
                          : contract.paymentType === ContractPaymentType.Full
                          ? 'Paid in full'
                          : 'Unknown'}
                      </td>
                      <td className={rightCell}>
                        {contract.paidAmount !== undefined
                          ? formatMoney(contract.paidAmount, contract.currency)
                          : '—'}
                      </td>
                      <td
                        className={mergeClassNames(
                          rightCell,
                          contract.remainingAmount === undefined
                            ? ''
                            : contract.remainingAmount > 0
                            ? 'text-customGold'
                            : 'text-customGreen'
                        )}
                      >
                        {contract.remainingAmount !== undefined
                          ? formatMoney(contract.remainingAmount, contract.currency)
                          : '—'}
                      </td>
                      <td className={leftCell}>
                        {contract.status !== undefined
                          ? CONTRACT_STATUS_LABELS[contract.status] ?? contract.status
                          : contract.hubspotStage ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </TableShell>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const StrategistsHandover = () => {
  const { data, isLoading, refetch, isFetching } = useGetStrategistsHandover({
    refetchOnWindowFocus: false,
  });
  const strategists = data?.strategists ?? [];
  const totals = {
    enrollments: strategists.reduce((sum, row) => sum + row.enrollmentsToReallocate, 0),
    students: strategists.reduce((sum, row) => sum + row.assignedStudents, 0),
    contracts: strategists.reduce((sum, row) => sum + row.contracts.count, 0),
  };

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold">Strategists Handover</h1>
          {data ? (
            <span className="text-xs text-customGrey">
              {strategists.length} education strategists · {totals.students} assigned students ·{' '}
              {totals.enrollments} active enrollments to reallocate · {totals.contracts} contracts ·
              generated {moment(data.generatedAt).format('D MMM YYYY HH:mm')}
            </span>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            buttonText="Export summary"
            icon={<DownloadIcon className="h-4 w-4" />}
            disabled={!strategists.length}
            onClick={() =>
              downloadCsv(
                `strategists-handover-${moment().format('YYYY-MM-DD')}.csv`,
                summaryCsv(strategists)
              )
            }
          />
          <Button
            variant="secondary"
            buttonText="Refresh"
            icon={<RefreshIcon className="h-4 w-4" />}
            isLoading={isFetching}
            onClick={() => refetch()}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-48 items-center justify-center">
          <LoadingSpinner size="normal" />
        </div>
      ) : !strategists.length ? (
        <EmptyState>No mentors are flagged as education strategists.</EmptyState>
      ) : (
        strategists.map((row) => <StrategistCard key={row.mentor._id} row={row} />)
      )}
    </div>
  );
};

export default StrategistsHandover;
